import type { PlainSearchParameters } from 'algoliasearch-helper';

import { useEffect, useRef } from 'react';

import {
  EXPONEA_USER_SEGMENT,
  IS_UTM_CPX_USER_SEGMENT,
} from '~/utilities/constants/localStorageKeys';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { storageAvailable } from '~/utilities/helpers';

const DEFAULT_SEGMENTATION = 'General';
const UTM_USER_SEGMENT = 'utm_cpx';

const syncSegment = () => {
  const hasStorage = storageAvailable('sessionStorage');
  const searchParams = new URLSearchParams(window.location.search);
  const urlSegment = searchParams.get(UTM_USER_SEGMENT);
  const existingSessionSegment = hasStorage ? sessionStorage.getItem(EXPONEA_USER_SEGMENT) : null;
  const segmentHash = urlSegment || existingSessionSegment;

  if (urlSegment && hasStorage) {
    sessionStorage.setItem(EXPONEA_USER_SEGMENT, urlSegment);
    sessionStorage.setItem(IS_UTM_CPX_USER_SEGMENT, 'true');
  }

  return segmentHash;
};

export const useAlgoliaRuleContexts = (): Pick<PlainSearchParameters, 'ruleContexts'> => {
  const {
    configuration: { userSegmentMappings, enableUserSegmentJourneys },
  } = useStaticContext();
  const segmentationRule = useRef<string>(DEFAULT_SEGMENTATION);
  const ruleContextsRef = useRef<string[]>([DEFAULT_SEGMENTATION]);

  useEffect(() => {
    const userSegmentHash = syncSegment();

    const segmentationFeatureAvailable = !!(
      userSegmentHash &&
      userSegmentMappings &&
      enableUserSegmentJourneys
    );

    if (segmentationFeatureAvailable) {
      const segment = userSegmentMappings[userSegmentHash];

      // exponea might assign an unknown segment hash that is not in `userSegmentMappings`, so we need to check before assigning
      if (segment) {
        segmentationRule.current = segment;
        ruleContextsRef.current = [segment];
      }
    }
  }, [userSegmentMappings, enableUserSegmentJourneys]);

  return { ruleContexts: ruleContextsRef.current };
};
